import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import SnackBar, { SnackTypes } from "../../UtilityComponents/SnackBar/SnackBar";
import { AcceptPaymentMethodEnum } from "../../../enums/AcceptPaymentMethodEnum";
import upiIcon from '../../../assets/images/AddNewGigPage/upi-icon.png';
import './PaymentSetupPageMainContainer.scss';
import { validateBankAccountNumber, validateIFSCCode, validateName, validateUPIId } from "../../../utils/validators";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PaymentModeFormSection from "../AddNewGigPageComponents/PaymentSetupPageComponents/PaymentModeFormSection";
import pageTextConfig from '../../../assets/configs/Add New Gig Page/PaymentSetupPage/textConfig';
import { BankAccountDetails, SellerPaymentFormData } from "../../../pages/AddNewGigPage/PaymentSetupPage/PaymentSetupPage";

export interface PaymentSetupPageMainContainerHandle {
    submit: () => void;
}

interface PaymentSetupPageMainContainerProps {
    onBackPress?: any;
    withButtonPanel?: boolean;
    showSingleButton?: boolean;
    savedFormData: SellerPaymentFormData;
    uponValidatedSubmission: (paymentFormData: SellerPaymentFormData) => void
}

const PaymentSetupPageMainContainer = forwardRef<PaymentSetupPageMainContainerHandle, PaymentSetupPageMainContainerProps>(({
    onBackPress,
    savedFormData,
    uponValidatedSubmission,
    withButtonPanel,
    showSingleButton = true,
}, ref) => {

    const [paymentFormData, setPaymentFormData] = useState<SellerPaymentFormData>(savedFormData)

    // Snackbar based status handling 
    const snackBarRef = useRef<{ showSnackbar(): any }>(null)
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error
    });
    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state })
        snackBarRef.current?.showSnackbar()
    }

    function updateFormData(fieldName: string, value: any) {
        setPaymentFormData((prevFormData) => ({
            ...prevFormData,
            [fieldName]: value,
        }));
    }

    function updateBankAccountDetails(event: any) {
        let bankFieldName = event.target.name
        let bankFieldValue = event.target.value
        let newBankDetails = paymentFormData.bankAccountDetails

        newBankDetails = {
            ...newBankDetails,
            [bankFieldName]: bankFieldValue
        }

        updateFormData('bankAccountDetails', newBankDetails);
    }

    // validation Section
    const performValidations = (): boolean => {

        switch (paymentFormData.selectedPaymentMode) {
            case AcceptPaymentMethodEnum.UPI:
                if (!validateUPI(paymentFormData.upiId)) return false;
                break;
            case AcceptPaymentMethodEnum.BankAccount:
                if (!validateBankDetails(paymentFormData.bankAccountDetails)) return false;
        }

        if (!paymentFormData.acknowledgementChecked) {
            showSnackbar("Please check the acknowledgement.", SnackTypes.error);
            return false
        }

        return true
    }

    const validateUPI = (upiId: string): boolean => {
        if (validateUPIId(upiId) === false) {
            showSnackbar("Please enter a valid UPI Id.", SnackTypes.error);
            return false;
        }
        return true
    }

    const validateBankDetails = (bankDetails: BankAccountDetails): boolean => {
        if (validateName(bankDetails.holderName) === false) {
            showSnackbar("Please enter a valid name.", SnackTypes.error);
            return false;
        } else if (validateBankAccountNumber(bankDetails.accountNumber) === false) {
            showSnackbar("Please enter a valid Account Number.", SnackTypes.error);
            return false;
        } else if (validateIFSCCode(bankDetails.bankIFSC) === false) {
            showSnackbar("IFSC Code entered is invalid.", SnackTypes.error);
            return false;
        }
        return true
    }

    const handleSubmission = () => {
        const validationsPassed = performValidations()

        if (validationsPassed) {
            uponValidatedSubmission(paymentFormData)
        }

    }

    useImperativeHandle(ref, () => ({
        submit: handleSubmission,
    }));

    const PaymentModeListSection = () => {

        return (
            <Box className="payment-options">
                <Box
                    className={
                        paymentFormData.selectedPaymentMode === AcceptPaymentMethodEnum.UPI ?
                            'payment-mode-list-item extra-border selected' :
                            'payment-mode-list-item extra-border'
                    }
                    onClick={() => {
                        updateFormData('selectedPaymentMode', AcceptPaymentMethodEnum.UPI)
                        updateFormData('bankAccountDetails', {
                            accountNumber: "",
                            bankIFSC: "",
                            holderName: "",
                            bankName: ""
                        })
                    }}>

                    <Box className="payment-mode-upi-image-container">
                        <img src={upiIcon} alt="UPI" />
                    </Box>

                </Box>

                <Box
                    className={
                        paymentFormData.selectedPaymentMode === AcceptPaymentMethodEnum.BankAccount ?
                            'payment-mode-list-item selected' :
                            'payment-mode-list-item'
                    }
                    onClick={() => {
                        updateFormData('selectedPaymentMode', AcceptPaymentMethodEnum.BankAccount)
                        updateFormData('upiId', "")
                    }}>
                    <AccountBalanceIcon sx={{ marginRight: "10px" }} />
                    {"Bank Account"}

                </Box>

            </Box>
        )
    }

    return (
        <Box className='payment-setup-main-container'>
            <Typography variant="h6" mb={"2vh"} fontWeight="600">
                {pageTextConfig.sectionTitle}
            </Typography>

            <Box className="payment-setup-form-container">
                <Box className="payment-form">
                    <Box className='left-panel'>
                        <PaymentModeListSection />
                    </Box>
                    <Box className='payment-option-details'>
                        <PaymentModeFormSection updateFormData={updateFormData} paymentFormData={paymentFormData} updateBankAccountDetails={updateBankAccountDetails} />
                    </Box>
                </Box>

                <Box id="disclaimer-container">
                    <FormControlLabel sx={{ textAlign: "left" }}
                        control={<Checkbox
                            checked={paymentFormData.acknowledgementChecked}
                            onChange={() => updateFormData('acknowledgementChecked', !paymentFormData.acknowledgementChecked)} />}
                        label={pageTextConfig.paymentAcknowledgement} />
                </Box>

                {withButtonPanel === true ?
                    <Box className="page-navigation-button-bar">
                        {onBackPress &&

                            <Button
                                className="secondary-black-button"
                                variant='outlined'
                                startIcon={<KeyboardDoubleArrowLeftIcon />}
                                size="large" sx={{ width: '200px', marginTop: '30px', marginRight: '15px' }}
                                onClick={onBackPress}
                            >Previous</Button>
                        }
                        <Button className="primary-black-button" variant='outlined' endIcon={<KeyboardDoubleArrowRightIcon />} size="large" sx={{ width: '200px', marginTop: '30px' }}
                            onClick={handleSubmission}
                        >Next</Button>
                    </Box> :
                    showSingleButton ? (
                        <Button 
                            className="primary-black-button" 
                            variant='outlined' 
                            size="large" 
                            sx={{ width: '200px', marginTop: '30px' }}
                            onClick={handleSubmission}
                        >
                            Save
                        </Button>
                    ) : null
                }
            </Box>
            <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />
        </Box>
    )
});

export default PaymentSetupPageMainContainer;